import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from '../../../components/atoms/page';
import { Vehicle } from '../../../types/vehicle';
import TableWidget, { TableColumn } from '../../../components/molecules/tableWidget';
import { TimeAgo } from '../../../components/timeAgo';
import { Box } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchCheckedInVehicles, fetchVehiclesWithUnknownTypes, VehicleUnknownType } from '../../../api/vehicles';
import { useSelectedWorkshop } from '../../../context/workshopContext';

type VehicleUnknownTypeRow = {
  id: string;
  vin: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number | string;
  checkedInUtc: string;
  workshopSessionId?: string;
};

const Vin = () => {
  const { isLoading } = useAuth0();
  const navigate = useNavigate();
  const { selectedOrganisationId: workshopId, isLoading: isLoadingWorkshops } = useSelectedWorkshop();
  const [vehiclesWithUnknownTypes, setVehiclesWithUnknownTypes] = useState<VehicleUnknownType[]>();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);

  useEffect(() => {
    if (!workshopId) {
      if (!isLoadingWorkshops) {
        setIsLoadingVehicles(false);
      }
      return;
    }

    const fetchVehiclesWithUnknownType = async () => {
      setIsLoadingVehicles(true);
      try {
        const data = await fetchVehiclesWithUnknownTypes(workshopId);
        setVehiclesWithUnknownTypes(data);

        const missingSessionId = (data ?? []).some((vehicle) => !vehicle.workshopSessionId);
        if (!missingSessionId) {
          setVehicles([]);
          return;
        }

        const checkedIn = await fetchCheckedInVehicles(workshopId);
        setVehicles(checkedIn ?? []);
      } catch (error) {
        console.log('error with fetching vehicles', error);
      } finally {
        setIsLoadingVehicles(false);
      }
    };

    fetchVehiclesWithUnknownType();
  }, [workshopId, isLoadingWorkshops]);

  const sessionIdByVin = useMemo(() => {
    const map = new Map<string, string>();
    for (const vehicle of vehicles) {
      if (vehicle.workshopSessionId) {
        map.set(vehicle.vin, vehicle.workshopSessionId);
      }
    }
    return map;
  }, [vehicles]);

  const tableData = useMemo<VehicleUnknownTypeRow[]>(
    () =>
      (vehiclesWithUnknownTypes ?? []).map((vehicle) => ({
        id: vehicle.vin,
        vin: vehicle.vin,
        vehicleMake: vehicle.vehicleMake.toLowerCase() === 'unknown' ? '-' : vehicle.vehicleMake,
        vehicleModel: vehicle.vehicleModel.toLowerCase() === 'unknown' ? '-' : vehicle.vehicleModel,
        vehicleYear: vehicle.vehicleYear,
        checkedInUtc: vehicle.checkedInUtc,
        workshopSessionId: vehicle.workshopSessionId,
      })),
    [vehiclesWithUnknownTypes]
  );

  const columns = useMemo<TableColumn[]>(
    () => [
      { id: 'vin', label: 'VIN' },
      { id: 'vehicleMake', label: 'Make' },
      { id: 'vehicleModel', label: 'Model' },
      { id: 'vehicleYear', label: 'Year', textColor: 'red' },
      {
        id: 'checkedInUtc',
        label: 'Checked in',
        format: (value) => <TimeAgo date={String(value)} />,
      },
    ],
    []
  );

  if (isLoading) {
    return <Box>Loading ...</Box>;
  }

  return (
    <Page title="Unmatched VINs">
      <TableWidget
        data={tableData}
        columns={columns}
        defaultSort="vin"
        themes={{ device: 'desktop' }}
        onClickRow={(_event, _id, row) => {
          const unmatched = row as VehicleUnknownTypeRow;
          const workshopSessionId = unmatched.workshopSessionId ?? sessionIdByVin.get(unmatched.vin);
          if (!workshopSessionId) {
            return;
          }
          navigate(`/vehicle/${unmatched.vin}/${workshopSessionId}`);
        }}
        pagination
        loadingStatus={isLoadingVehicles ? 'loading' : undefined}
      />
    </Page>
  );
};

export default Vin;
